import { useMemo, useState } from "react";

import { GetDefaultLayout } from "../../../Drawers/Records/ModuleLayoutSelector";
import UpdateRecordDrawer from "../../../Drawers/Records/UpdateRecordDrawer";
import { RecordSummarySection } from "../../../Records/RecordDetailLayout";
import { ReusableViewIDs, View } from "../../../../types/PageInterfaces";
import AppTablePagination from "../../../AppTable/AppTablePagination";
import useRecordMetadata from "../../../../hooks/useRecordMetadata";
import SkeletonLoader from "../../../../elements/SkeletonLoader";
import useRecordImage from "../../../../hooks/useRecordImage";
import AppEmptyState from "../../../../elements/AppEmptyState";
import FolderCardRecord from "./FolderCard/FolderCardRecord";
import { Module } from "../../../../types/TenantInterfaces";
import ImageCardRecord from "./ImageCard/ImageCardRecord";
import { SharedViewModes } from "../../PageViewRenderer";
import useFieldMap from "../../../../hooks/useFieldMap";
import { CardsLayoutI } from "../usePageViewTable";
import NoCardsLayouts from "./NoCardsLayouts";
import CardRecord from "./CardRecord";

interface Props {
  currentModule: Module;
  records: any[];
  recordsLoading: boolean;
  query: any;
  setQuery: (q: any) => any;
  pagination: any;
  error: any;
  props: any;
  selectedRecords?: { [key: number]: boolean };
  setSelectedRecords?: (newObj: object) => void;
  view: View;
  parentViewID?: string;
  viewMode: SharedViewModes;
  onOpenRecordName: any;
  foundLayout: CardsLayoutI & { id: string };
  reusableViews?: ReusableViewIDs;
  commentsWidget?: boolean;
  CustomCardFooter?: ({
    record,
  }: {
    record: Record<string, any>;
  }) => React.ReactNode;
}

const emptyLayouts: [] = [];
const skeletonLoaders = [1, 2, 3, 4, 5, 6, 7, 8, 9];

/**
 * PageViewCards - Handle the render of the "Cards" style view. Type: Cards
 */
const PageViewCards = ({
  currentModule,
  records,
  recordsLoading,
  query,
  setQuery,
  pagination,
  error,
  selectedRecords,
  setSelectedRecords,
  view,
  parentViewID,
  viewMode,
  onOpenRecordName,
  foundLayout,
  reusableViews,
  commentsWidget,
  CustomCardFooter,
}: Props) => {
  const [updateRecordID, setUpdateRecordID] = useState<string | null>(null);
  const fieldMap: { [key: string]: any } = useFieldMap(currentModule?.APIName);
  const { imageField, imageFieldPresentation } = useRecordImage({
    module: currentModule,
  });
  const { metadataField, metadataFieldType } = useRecordMetadata({
    module: currentModule,
  });

  const cardLayouts = view?.CardsLayouts ?? emptyLayouts;
  const frontFields = foundLayout?.Fields || query?.Fields;

  const layout = useMemo(() => {
    return GetDefaultLayout(currentModule, view?.DefaultRecordLayout) || null;
  }, [currentModule, view?.DefaultRecordLayout]);

  const frontFieldsSection = useMemo<RecordSummarySection>(() => {
    if (!fieldMap || !Array.isArray(frontFields)) {
      return {
        id: "Cards",
        Type: "RecordSummarySection",
        Fields: [],
        Title: "",
      };
    }

    const isSmall = frontFields.length <= 5;

    return {
      id: "Cards",
      Title: "",
      Type: "RecordSummarySection",
      Fields: frontFields.flatMap((f) => {
        if (["id", "_RecordName"].includes(f)) return [];

        return [
          {
            Field: fieldMap[f],
            ColSpan: isSmall ? 1 : 1,
          },
        ];
      }),
    };
  }, [fieldMap, frontFields]);

  const loading = recordsLoading;

  if (!cardLayouts?.length) {
    return (
      <NoCardsLayouts
        currentModule={currentModule}
        viewID={view?._id!}
        parentViewID={parentViewID}
        viewMode={viewMode}
      />
    );
  }

  if (!loading && records?.length === 0) {
    return (
      <AppEmptyState
        title="No Results"
        description="There are no records matching the current filter."
        icon="mdi-archive-search"
      />
    );
  }

  return (
    <div className="tw:md:mx-auto tw:ml-[-24px] tw:mr-[-8px]">
      <div className="tw:flex tw:flex-wrap tw:my-6 tw:md:justify-start tw:justify-center tw:mx-auto tw:gap-6">
        {loading &&
          skeletonLoaders.map((num) => {
            return (
              <SkeletonLoader
                key={num}
                css="tw:my-2 tw:mx-0 tw:md:mx-2"
                maxWidth={350}
                height={270}
              />
            );
          })}
        {!loading &&
          Array.isArray(records) &&
          records.length > 0 &&
          records.map((record, i) => {
            const onSelect = setSelectedRecords
              ? () =>
                  setSelectedRecords((prev: any) => {
                    const newRecords = { ...prev };

                    if (newRecords[i]) delete newRecords[i];
                    else newRecords[i] = true;

                    return newRecords;
                  })
              : undefined;

            if (foundLayout?.CardType === "Image") {
              return (
                <ImageCardRecord
                  key={record?.id}
                  record={record}
                  cardLayout={foundLayout}
                  frontFieldsSection={frontFieldsSection}
                  isSelected={selectedRecords ? !!selectedRecords[i] : false}
                  onSelect={onSelect}
                  currentModule={currentModule}
                  setOpenRecordData={onOpenRecordName}
                  imageField={imageField}
                  imageFieldPresentation={imageFieldPresentation}
                  metadataField={metadataField}
                  metadataFieldType={metadataFieldType}
                  setUpdateRecordID={setUpdateRecordID}
                  viewID={view?.id}
                  cardImageField={foundLayout?.CardImage || ""}
                  reusableViews={reusableViews}
                  commentsWidget={commentsWidget}
                  CustomCardFooter={CustomCardFooter}
                />
              );
            }

            if (foundLayout.CardType === "Folder") {
              return (
                <FolderCardRecord
                  key={record?.id}
                  record={record}
                  cardLayout={foundLayout}
                  frontFieldsSection={frontFieldsSection}
                  isSelected={selectedRecords ? !!selectedRecords[i] : false}
                  onSelect={onSelect}
                  currentModule={currentModule}
                  setOpenRecordData={onOpenRecordName}
                  imageField={imageField}
                  imageFieldPresentation={imageFieldPresentation}
                  metadataField={metadataField}
                  metadataFieldType={metadataFieldType}
                  setUpdateRecordID={setUpdateRecordID}
                  viewID={view?.id}
                  cardImageField={foundLayout?.CardImage}
                  reusableViews={reusableViews}
                />
              );
            }

            return (
              <CardRecord
                key={record?.id}
                record={record}
                imageField={imageField}
                imageFieldPresentation={imageFieldPresentation}
                currentModule={currentModule}
                setOpenRecordData={onOpenRecordName}
                metadataField={metadataField}
                metadataFieldType={metadataFieldType}
                setUpdateRecordID={setUpdateRecordID}
                fieldsSection={frontFieldsSection}
                isSelected={selectedRecords ? !!selectedRecords[i] : false}
                viewID={view?.id}
                onSelect={onSelect}
                reusableViews={reusableViews}
              />
            );
          })}
      </div>
      <AppTablePagination
        key={view?._id}
        records={records}
        pagination={pagination}
        query={query}
        setQuery={setQuery}
        error={error}
        loading={recordsLoading}
        module={currentModule}
        containerCSS="pr-6"
        sticky
      />
      <UpdateRecordDrawer
        open={!!updateRecordID}
        onClose={() => setUpdateRecordID(null)}
        recordID={updateRecordID!}
        moduleAPIName={currentModule?.APIName}
        layout={layout}
      />
    </div>
  );
};

export default PageViewCards;
